/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import { useCSS } from '../../../provider/CSSProvider';
import style from './DatePicker.scss?inline';
import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import WarningIcon from '@mui/icons-material/Warning';
import { Tooltip } from '@mui/material';
import { nonEmptyString } from '../../../utils/util';

export interface DatePickerProps {
  readonly className?: string;
  readonly label?: string;
  readonly required?: boolean; // defaults to false
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onUnFocus?: (newValue: string | undefined) => void;
}

export const DatePickerFormat = 'YYYY-MM-DD';

export function DatePicker({ className, label, value, onChange, onUnFocus, required }: DatePickerProps) {
  useCSS(style);

  const [showInvalidIcon, setShowInvalidIcon] = useState<string | null>(null);
  const [datePickerValue, setDatePickerValue] = useState<string | null | undefined>(value);

  useEffect(() => {
    setDatePickerValue((oldValue) => {
      if (value === '' || datePickerValueIsValid(value)) {
        return value;
      } else {
        return oldValue;
      }
    });
  }, [value]);

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    const isValid = event.target.validity.valid && datePickerValueIsValid(newValue);
    setDatePickerValue(newValue);

    if (!isValid && newValue !== '') {
      return;
    }
    onChange(newValue);
  };

  // We only output dates with year between 1900 and 9999.
  // Otherwise, dayJs (used by the parent component) will prepend a 19 in front of the year.
  // This causes the date picker to update and the cursor to jump.
  const datePickerValueIsValid = (value: string): boolean => {
    const year = Number(value!.split('-')[0]);

    return dayjs(value).isValid() && year != null && year >= 1900 && year <= 9999;
  };

  function invalidIcon() {
    if (showInvalidIcon != null) {
      return (
        <div className={'datepicker-input-icons'}>
          <Tooltip arrow title={showInvalidIcon}>
            <WarningIcon data-testid='invalid-date' className='warning-symbol' color='warning' />
          </Tooltip>
        </div>
      );
    }
  }

  function handleDateSave(event: React.FocusEvent<HTMLInputElement>) {
    const isValid = event.target.validity.valid;
    const value = event.target.value;

    const notRequiredHint = required ? '' : ' oder löschen Sie die Eingabe komplett';
    setShowInvalidIcon(
      isValid ? null : 'Bitte geben Sie ein vollständiges und gültiges Datum ab dem 01.01.1900 ein' + notRequiredHint
    );

    if (onUnFocus) {
      // empty input for deletion
      if (isValid && value == '') {
        onUnFocus('');
        return;
      }
      if (isValid && nonEmptyString(event.target.value) && datePickerValueIsValid(event.target.value)) {
        onUnFocus(event.target.value);
      }
    }
  }

  return (
    <fieldset className={`datepicker-fieldset ${className ?? ''}`}>
      {label && <legend className='datepicker-legend'>{label}</legend>}
      <input
        type='date'
        className='datepicker-input'
        value={datePickerValue ?? ''}
        data-testid={label ?? 'Tag'}
        onChange={handleDateChange}
        onBlur={handleDateSave}
        onFocus={() => setShowInvalidIcon(null)}
        required={required ?? false}
        min='1900-01-01'
        max='9999-12-31'
      />
      {invalidIcon()}
    </fieldset>
  );
}
